import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Send, MapPin, Mail, Loader2, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const EMPTY_FORM = {
  name: '',
  email: '',
  subject: '',
  message: '',
  subscribeNewsletter: false,
  requestType: 'contact'
};

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    const onPrefill = (e) => {
      setFormData((prev) => ({
        ...prev,
        subject: e.detail?.subject || prev.subject,
        requestType: e.detail?.requestType || 'contact'
      }));
    };
    window.addEventListener('prefill-contact', onPrefill);
    return () => window.removeEventListener('prefill-contact', onPrefill);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'email') setEmailError('');
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(formData.email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, { ...formData, email: formData.email.trim() });
      toast.success(
        formData.requestType === 'ai_readiness'
          ? 'Your AI Readiness Assessment request has been received. We will be in touch.'
          : 'Message sent successfully. We will be in touch.'
      );
      setFormData(EMPTY_FORM);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const emailIssue = Array.isArray(detail) && detail.some((d) => d?.loc?.includes('email'));
      if (emailIssue) setEmailError('Please enter a valid email address.');
      toast.error(emailIssue ? 'Please check your email address.' : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="contact-section"
      style={{ background: '#050505' }}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-16" />
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-12 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Contact
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {formData.requestType === 'ai_readiness' && (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-3"
                  style={{ border: '1px solid rgba(201,162,39,0.35)', background: 'rgba(201,162,39,0.08)' }}
                  data-testid="contact-ai-readiness-badge"
                >
                  <p className="text-xs uppercase tracking-[0.2em]" style={{ color: '#c9a227' }}>
                    AI Readiness Assessment Request
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, requestType: 'contact', subject: '' }))}
                    className="p-1 hover:bg-white/5 transition-colors"
                    aria-label="Switch to a general enquiry"
                    data-testid="contact-ai-readiness-clear"
                  >
                    <X className="w-3.5 h-3.5 text-white/50" />
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-widest text-[#c9a227]/70 mb-2">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="w-full input-dark"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-widest text-[#c9a227]/70 mb-2">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full input-dark"
                    style={emailError ? { borderColor: 'rgba(220,80,80,0.6)' } : undefined}
                    placeholder="your@email.com"
                    aria-invalid={!!emailError}
                    data-testid="contact-email-input"
                  />
                  {emailError && (
                    <p className="mt-2 text-xs" style={{ color: 'rgba(230,110,110,0.9)' }} data-testid="contact-email-error">
                      {emailError}
                    </p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="subject" className="block text-xs uppercase tracking-widest text-[#c9a227]/70 mb-2">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                  placeholder="How can we help?"
                  data-testid="contact-subject-input"
                />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-xs uppercase tracking-widest text-[#c9a227]/70 mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full input-dark resize-none"
                  placeholder="Your message..."
                  data-testid="contact-message-input"
                />
              </div>

              {/* Newsletter Subscription */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="subscribeNewsletter"
                  name="subscribeNewsletter"
                  checked={formData.subscribeNewsletter}
                  onChange={handleChange}
                  className="w-5 h-5 rounded-none border-[#c9a227]/30 bg-[#c9a227]/5 checked:bg-[#c9a227] cursor-pointer accent-[#c9a227]"
                  data-testid="contact-newsletter-checkbox"
                />
                <label htmlFor="subscribeNewsletter" className="text-white/60 text-sm cursor-pointer">
                  Subscribe to receive updates and insights from Celestial Minds
                </label>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary text-xs uppercase tracking-widest flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 
                className="text-xl text-white mb-4"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Get in Touch
              </h3>
              <p className="text-white/50 leading-relaxed">
                For inquiries regarding partnerships, investments, or collaborations, please use the form or reach us directly.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-[#c9a227] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white mb-1">Office Address</h4>
                  <p className="text-white/50 text-sm leading-relaxed">
                    One Central Dubai,<br />
                    Level 3 & 5, One Central, The Offices 3,<br />
                    Dubai World Trade Center,<br />
                    Sheikh Zayed Road,<br />
                    PO Box 9573, Dubai, United Arab Emirates
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-5 h-5 text-[#c9a227] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-white mb-1">Email</h4>
                  <a 
                    href="mailto:invitation@celestialmindsfze.com" 
                    className="text-[#c9a227] hover:text-[#e8d5a3] transition-colors text-sm"
                    data-testid="contact-email-link"
                  >
                    invitation@celestialmindsfze.com
                  </a>
                </div>
              </div>
            </div>

            <div 
              className="p-6 border border-[#c9a227]/20"
              style={{ background: 'rgba(10, 18, 37, 0.5)' }}
            >
              <p className="text-white/40 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
                Engagements are selective and discreet. We respond to inquiries where there is potential alignment with our vision and values.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
